import { useEffect, useRef } from 'react';
import { useDiagramStore } from '../stores/diagramStore';
import { useCompassGraph } from './useCompassGraph';
import { useFolderData } from './useFolderData';
import { useDiagramInfo } from './useDiagramInfo';
import { useNetworkReviewBaseline } from '../domains/network/hooks/useNetworkReviewBaseline';
import { useCompassViewPrefs, focusKey, type FocusSpec } from '../stores/compassViewPrefsStore';
import { getCommandManager } from './useCommandManager';
import { CompositeCommand, type ICommand } from '../core/commands/ICommand';
import {
  ChangeFocusCommand,
  ChangeZoomLevelCommand,
} from '../domains/compass/commands/CompassExplorerCommands';
import { ChangeExplorerLensCommand } from '../domains/compass/commands/explorer/ChangeExplorerLensCommand';
import { buildExplorerScene } from '../domains/compass/services/buildExplorerScene';
import { decodeCompassUrlState } from '../domains/compass/services/explorer/compassUrlState';
import { findLiveReviewViewById } from '../domains/compass/views/liveReviewViews';
import { projectCompassScenario } from '../domains/compass/scenarios/compassScenarioProjector';
import { LensRegistry } from '../domains/compass/lenses';
import { presetAsLens } from '../domains/compass/lenses/presetAsLens';
import { useViewRulesStore } from '../core/assistant/stores/viewRulesStore';
import { useScenariosStore } from '../core/assistant/stores/scenariosStore';
import type { LensDefinition } from '../domains/compass/models/explorer/ExplorerLens';
import type { Scene, Node, Edge } from '../core/models/scene';
import type { FullPreset } from '../core/assistant/models/ViewRules';
import type { ScenarioDelta } from '../core/data-profiles/scenarios/ScenarioDelta';
import {
  applyNetworkReviewBaselineSceneDeltas,
  isNetworkReviewBaselineDriftFocus,
  withNetworkReviewBaselineDeltas,
} from '../domains/network/services/NetworkReviewBaselineOverlayService';
import '../domains/compass/lenses/index';

/**
 * useCompassExplorerSync
 *
 * Activates for diagrams whose `viewDefinition.type === 'compass-explorer'`.
 * Loads the folder's knowledge graph once, then recomputes the on-canvas
 * scene every time focus/zoom changes in `CompassViewPrefsStore` and
 * pushes it into `diagramStore.loadScene()`.
 *
 * The underlying Yjs doc is left empty (or whatever annotations the
 * user has added); this hook never writes. Focus/zoom are ephemeral
 * client-side UI state — they live in the store, sync to URL hash,
 * and route through the command framework for undo/redo.
 *
 * No-op when the diagram isn't a compass-explorer marker.
 */
export function useCompassExplorerSync(diagramId: string | undefined): {
  isExplorer: boolean;
  isLoading: boolean;
  error: unknown;
} {
  const { data: diagramInfo } = useDiagramInfo(diagramId);
  const isExplorer = diagramInfo?.viewDefinition?.type === 'compass-explorer';
  const folderId = diagramInfo?.folderId;
  const hydratedUrlStateRef = useRef<string | null>(null);

  // Folder lookup gets us orgId without requiring a URL query param.
  const { data: folderData } = useFolderData(isExplorer ? folderId : undefined);
  const orgId = folderData?.folder?.orgId;

  const { data: graphData, isLoading, error } = useCompassGraph(
    isExplorer ? folderId : undefined,
    isExplorer ? orgId : undefined,
  );
  const { data: baselineResult } = useNetworkReviewBaseline(isExplorer ? folderId : undefined);

  const focus = useCompassViewPrefs((s) => s.focus);
  const zoom = useCompassViewPrefs((s) => s.zoomLevel);
  const expandedFrameIds = useCompassViewPrefs((s) => s.expandedFrameIds);
  const expandedEdgeIds = useCompassViewPrefs((s) => s.expandedEdgeIds);
  const collapseEdges = useCompassViewPrefs((s) => s.collapseEdges);
  const lensId = useCompassViewPrefs((s) => s.lensId);
  const activeFilterChipIds = useCompassViewPrefs((s) => s.activeFilterChipIds);
  const fullPresets = useViewRulesStore((s) => s.fullPresets);
  const activeScenario = useScenariosStore((s) =>
    s.activeScenarioId ? s.scenarioById.get(s.activeScenarioId) ?? null : null,
  );
  // Selection is intentionally NOT subscribed here. Earlier this
  // hook re-ran `buildExplorerScene` on every selection change so a
  // subnet/VPC selection could auto-expand its aggregated edges —
  // but the side effect was a *full* graph rebuild (2003 nodes
  // through ELK) on every click, which made panning feel laggy
  // because clicking before a drag flipped selection. Aggregated-
  // edge expansion is now a click handler concern on the canvas,
  // not a scene-build concern.
  const loadScene = useDiagramStore((s) => s.loadScene);
  const setInitialLoading = useDiagramStore((s) => s.setInitialLoading);
  const setStage = useDiagramStore((s) => s.setStage);
  // Gate the panel-clearing pan to once per diagram. After the user
  // pans/zooms intentionally we never re-apply it — they own the
  // viewport from that point on.
  const pannedForPanelRef = useRef<string | null>(null);

  useEffect(() => {
    if (!isExplorer || !diagramId || typeof window === 'undefined') return;
    const hydrationKey = `${diagramId}:${window.location.search}`;
    if (hydratedUrlStateRef.current === hydrationKey) return;
    hydratedUrlStateRef.current = hydrationKey;

    const urlState = decodeCompassUrlState(window.location.search);
    const commands: ICommand[] = [];
    const prefs = useCompassViewPrefs.getState();

    // When the URL carries a named architecture view (`?view=<id>`),
    // its declared kind + presetId + lensId + chips are authoritative.
    // The view URL builder omits `focus=` for overlay views (they want
    // the whole estate visible), but if the prefs store has a stale
    // `focus` from a previous session — say the user was drilled into
    // a VPC then clicked Cost Waste from the Architecture-views card —
    // that stale focus would persist and the slicer would render the
    // VPC subgraph instead of the full estate. Net effect for the
    // user: "Cost Waste is always empty".
    //
    // Resolution: derive the intended focus from `view`, falling back
    // to the URL-encoded focus, then to the existing prefs focus.
    const viewParam = new URLSearchParams(window.location.search).get('view');
    const activeView = findLiveReviewViewById(viewParam);
    const intendedFocus: FocusSpec | null = activeView
      ? activeView.kind === 'slice'
        ? { kind: 'preset', presetId: activeView.presetId ?? activeView.id, label: activeView.title }
        : { kind: 'overview' }
      : urlState.focus ?? null;
    if (intendedFocus && focusKey(prefs.focus) !== focusKey(intendedFocus)) {
      commands.push(new ChangeFocusCommand(intendedFocus, prefs.focus));
    } else if (urlState.focus && focusKey(prefs.focus) !== focusKey(urlState.focus)) {
      commands.push(new ChangeFocusCommand(urlState.focus, prefs.focus));
    }
    if (urlState.zoom && prefs.zoomLevel !== urlState.zoom) {
      commands.push(new ChangeZoomLevelCommand(urlState.zoom, prefs.zoomLevel));
    }
    if (urlState.lensId && prefs.lensId !== urlState.lensId) {
      commands.push(new ChangeExplorerLensCommand(urlState.lensId, prefs.lensId));
    }
    if (urlState.searchQuery) {
      prefs.setSearchQuery(urlState.searchQuery);
    }

    // Quick-filter chips can be deep-linked through the `chips` URL
    // param so the Architecture-views entry cards can land the user
    // on a pre-filtered view (e.g. Ownership Gaps highlighting the
    // "unowned" chip on top of the full canvas).
    const chipsRaw = new URLSearchParams(window.location.search).get('chips');
    if (chipsRaw) {
      const desired = chipsRaw.split(',').map((s) => s.trim()).filter(Boolean);
      const current = new Set(prefs.activeFilterChipIds);
      const same =
        desired.length === current.size && desired.every((id) => current.has(id));
      if (!same) {
        prefs.clearFilterChips();
        for (const id of desired) prefs.toggleFilterChip(id);
      }
    }

    if (commands.length === 1) {
      void getCommandManager().execute(commands[0]);
    } else if (commands.length > 1) {
      void getCommandManager().execute(new CompositeCommand('Open Compass URL state', commands));
    }
  }, [diagramId, isExplorer]);

  // `buildExplorerScene` is async — the ELK edge-routing pass
  // returns a promise. Apply only the newest build so quick L2/L3/L4
  // switches don't replay stale scenes after the latest one wins.
  useEffect(() => {
    if (!isExplorer || !graphData) {
      return;
    }
    let cancelled = false;
    const buildFocus = effectiveCompassBuildFocus(focus, activeScenario);
    const showBaselineDrift = !activeScenario && isNetworkReviewBaselineDriftFocus(focus);
    const explorerGraph = withNetworkReviewBaselineDeltas(
      {
        nodes: graphData.nodes,
        edges: graphData.edges,
        documents: graphData.documents,
        jobs: [],
      },
      baselineResult,
      showBaselineDrift,
    );
    buildExplorerScene(
      explorerGraph,
      buildFocus,
      zoom,
      {
        expandedFrameIds,
        expandedEdgeIds,
        collapseEdges,
        activeFilterChipIds,
      },
    ).then((built) => {
      if (cancelled) return;
      const lens = resolveCompassLens(lensId, fullPresets, built.nodes);
      const lensScene = lens ? applyLensToScene(built, lens, folderId) : built;
      const baselineScene = applyNetworkReviewBaselineSceneDeltas(
        lensScene,
        explorerGraph,
        baselineResult,
        showBaselineDrift,
      );
      const scene = activeScenario
        ? applyActiveScenarioToScene(baselineScene, activeScenario.delta)
        : baselineScene;
      loadScene(lockSynthesizedScene(scene), { preserveSelection: true });
      // The realtime projection hook is disabled for Explorer diagrams
      // (see App.tsx: enabled: !isExplorer), so nothing else will flip
      // the initial-loading flag — the overlay would stick otherwise.
      setInitialLoading(false);
      // Pan the canvas right on first scene load so the leftmost
      // content isn't hidden behind the assistant panel (which opens
      // by default for the Compass Explorer at 380px wide). Gated by
      // a per-diagram ref — once the user pans or zooms we never
      // re-apply, so this only affects the cold-open viewport.
      if (diagramId && pannedForPanelRef.current !== diagramId) {
        pannedForPanelRef.current = diagramId;
        // Panel is 380px + ~30px of breathing room → 410px viewport shift.
        // 24px y offset gives the first row of cards a sliver of top
        // padding so they don't sit flush against the toolbar.
        setStage({ offsetX: 410, offsetY: 24 });
      }
    });
    return () => {
      cancelled = true;
    };
  }, [
    isExplorer,
    graphData,
    focus,
    zoom,
    expandedFrameIds,
    expandedEdgeIds,
    collapseEdges,
    lensId,
    activeFilterChipIds,
    fullPresets,
    activeScenario,
    baselineResult,
    folderId,
    loadScene,
    setInitialLoading,
  ]);

  return {
    isExplorer: !!isExplorer,
    isLoading: !!isExplorer && isLoading,
    error,
  };
}

function effectiveCompassBuildFocus(
  focus: FocusSpec,
  activeScenario: { delta: ScenarioDelta<any, any> } | null,
): FocusSpec {
  if (!activeScenario) return focus;
  if (focus.kind === 'preset' && focus.presetId === 'planned-changes') {
    return { kind: 'overview' };
  }
  return focus;
}

function resolveCompassLens(
  lensId: string,
  fullPresets: readonly FullPreset[],
  sceneNodes: readonly Node[],
): LensDefinition | undefined {
  const preset = fullPresets.find((candidate) => candidate.id === lensId);
  if (preset) return presetAsLens(preset, sceneNodes) ?? undefined;
  return LensRegistry.get(lensId);
}

function applyActiveScenarioToScene(
  scene: Scene,
  delta: ScenarioDelta<any, any>,
): Scene {
  const projected = projectCompassScenario(scene.nodes, scene.edges, delta);
  return {
    ...scene,
    nodes: projected.nodes,
    edges: projected.edges,
  };
}

/**
 * Compass Explorer scenes are synthesised client-side from the folder
 * knowledge graph (see `buildExplorerScene`) and re-emitted whenever
 * focus / zoom / lens / chips / scenarios change. Positions come from
 * `InfraLayoutEngine` + ELK; the underlying Yjs doc is never written
 * by this hook (see the contract at the top of `useCompassExplorerSync`).
 *
 * That makes every node in this scene **system-owned, not user-owned**:
 * dragging a card would either snap back on the next rebuild or — for
 * aggregated `frame:<id>` endpoints — fail to update the routed edges
 * at all. Stamp the canonical `locked` flag from `Node` (see
 * `src/core/models/scene.ts`) so the existing canvas-wide lock pipeline
 * (`DiagramCanvasWithTransform`, `FrameRenderer`, transform handlers,
 * text editing, copy/cut/paste, overlays) treats the entire scene as
 * read-only. The intent surfaces in the canvas: cards still hover,
 * click-to-drill still fires, but the user can't strand them in a
 * position the scene model can't preserve.
 *
 * When user-owned annotations land on this surface they should be
 * rendered as a separate overlay layer above the synthesised scene
 * (and stored in the Yjs doc), not by lifting this lock.
 */
function lockSynthesizedScene(scene: Scene): Scene {
  const nodes: Node[] = scene.nodes.map((node) =>
    node.locked && node.lockOwner === 'projection'
      ? node
      : { ...node, locked: true, lockOwner: 'projection' as const },
  );
  // Edges are aggregate / routed and not user-editable either; the
  // edge model has no `locked` field today, but emit a frozen copy so
  // mutators downstream don't accidentally splice through.
  const edges: Edge[] = scene.edges.slice();
  return { ...scene, nodes, edges };
}

function applyLensToScene(scene: Scene, lens: LensDefinition, folderId: string | undefined): Scene {
  return {
    ...scene,
    nodes: scene.nodes.map((node) => {
      const decoration = lens.decorate(node, {
        folderId: folderId ?? '',
        now: new Date(),
      });
      if (!decoration) return node;
      // The bubble card renderer reads `attrs.lens.color` directly,
      // but L4/L5 leaf shapes render through the generic
      // `ManifestSvgNode` pipeline which only knows about
      // `attrs.accentColor` (view-profile scoring already uses that
      // hook to override the manifest's default service colour).
      // Projecting the lens colour through `accentColor` paints the
      // shape's icon tile in the heatmap colour — the visible payoff
      // for cost / waste / utilisation lenses at resource level.
      // Skips frames and bubbles (they own their own chrome).
      const isLeaf =
        node.type !== 'frame' && node.type !== 'compass_bubble';
      const wantsAccent =
        isLeaf && !!decoration.color && !decoration.dataMissing;
      return {
        ...node,
        attrs: {
          ...(node.attrs ?? {}),
          ...(wantsAccent ? { accentColor: decoration.color } : {}),
          lens: decoration,
        },
      };
    }),
  };
}
